/**
 * Turn a saved picture by hand, and keep it turned.
 *
 * A photo taken sideways on a browser that hides the phone's orientation
 * (Brave blocks motion sensors by default) cannot be turned automatically.
 * So each picture carries two translucent buttons — rotate left at its top
 * left corner, rotate right at its top right — on the thumbnail and in the
 * full view alike. A turn made in either shows in both, is sent to the
 * server as a note on the attachment, and comes back on every reload and
 * every other browser.
 *
 * The pixels on the server are never rewritten. What the page shows is the
 * original drawn onto a canvas at the chosen angle, so layout needs no
 * knowledge of the turn: a rotated image is simply an image of the rotated
 * shape.
 */

import { API_BASE, state } from './state.js';
import { getHeaders } from './api.js';

const ANGLES = [0, 90, 180, 270];

/** Draw `source` (a loaded <img>) turned by `deg`, capped to `maxEdge` px. */
function drawRotated(source, deg, maxEdge) {
    const w = source.naturalWidth;
    const h = source.naturalHeight;
    if (!w || !h) return null;
    const scale = maxEdge ? Math.min(1, maxEdge / Math.max(w, h)) : 1;
    const sw = Math.round(w * scale);
    const sh = Math.round(h * scale);
    const quarter = deg === 90 || deg === 270;
    const canvas = document.createElement('canvas');
    canvas.width = quarter ? sh : sw;
    canvas.height = quarter ? sw : sh;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((deg * Math.PI) / 180);
    ctx.drawImage(source, -sw / 2, -sh / 2, sw, sh);
    return canvas.toDataURL('image/jpeg', 0.9);
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = src;
    });
}

/**
 * Show `img` at `deg`. The original source is remembered on the element the
 * first time, so any angle (including back to 0) starts from the real bytes
 * rather than from an already-turned copy.
 */
export async function applyRotation(img, deg, maxEdge = null) {
    if (!img) return;
    if (!img.dataset.origSrc) img.dataset.origSrc = img.getAttribute('src') || '';
    const orig = img.dataset.origSrc;
    img.dataset.rotation = String(deg);
    if (!deg) {
        if (img.getAttribute('src') !== orig) img.src = orig;
        return;
    }
    try {
        const source = await loadImage(orig);
        // A newer turn may have been asked for while this one decoded.
        if (img.dataset.rotation !== String(deg)) return;
        const url = drawRotated(source, deg, maxEdge);
        if (url) img.src = url;
    } catch {
        /* the original stays; a turn that cannot be drawn is not an error */
    }
}

export function nextAngle(current, direction) {
    const i = ANGLES.indexOf(Number(current) || 0);
    return ANGLES[(i + (direction === 'left' ? 3 : 1)) % 4];
}

/** Remember the turn on the server. Best effort: the view already turned. */
export async function saveRotation(messageId, attachmentId, deg) {
    if (!attachmentId || !state.sessionId || state.capsuleMode) return;
    // A message just sent has no id on the page until a reload; the server
    // then finds the picture by its attachment id within this session.
    const path = messageId
        ? `/sessions/${state.sessionId}/messages/${messageId}/attachments/${encodeURIComponent(attachmentId)}`
        : `/sessions/${state.sessionId}/attachments/${encodeURIComponent(attachmentId)}`;
    try {
        await fetch(`${API_BASE}${path}`, {
            method: 'PATCH', headers: getHeaders(), body: JSON.stringify({ rotation: deg }),
        });
    } catch {
        /* the next reload shows the last saved turn; nothing else to do */
    }
}

/**
 * The two corner buttons. `onTurn(direction)` is called with 'left' or
 * 'right'; the caller decides what is turned and where it is saved.
 */
export function rotateButtons(onTurn, className = 'img-rotate') {
    const frag = document.createDocumentFragment();
    for (const [dir, glyph, title] of [
        ['left', '↺', 'Rotate left'],
        ['right', '↻', 'Rotate right'],
    ]) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = `${className} ${className}-${dir}`;
        b.textContent = glyph;
        b.title = title;
        b.setAttribute('aria-label', title);
        b.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            onTurn(dir);
        });
        frag.appendChild(b);
    }
    return frag;
}

/**
 * Turn a thumbnail and everything that mirrors it. The thumbnail is the
 * source of truth on the page: its dataset carries the attachment id, the
 * message id, the original source and the current angle, and the full view
 * reads those rather than keeping its own copy.
 */
export async function turnThumbnail(thumb, direction) {
    const deg = nextAngle(thumb.dataset.rotation, direction);
    await applyRotation(thumb, deg, 512);
    saveRotation(thumb.dataset.messageId, thumb.dataset.attachmentId, deg);
    document.dispatchEvent(new CustomEvent('attachment-rotated', { detail: { thumb, deg } }));
    return deg;
}
